'use client';

import { useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';
import { CheckCircle, ArrowRight, BookOpen, ClipboardCheck } from 'lucide-react';
import { useTranslations } from 'next-intl';

const DASHBOARD_URL = process.env.NEXT_PUBLIC_DASHBOARD_URL || 'http://localhost:3502';

interface Props {
  items?: string[];
  /** Which product types were in this purchase — determines which button is shown first */
  purchasedTypes?: ('module' | 'course' | 'event')[];
  onClose: () => void;
}

export function PurchaseCelebration({ items, purchasedTypes = [], onClose }: Props) {
  const t = useTranslations('purchaseCelebration');
  const hasCourse = purchasedTypes.includes('course');
  const hasModule = purchasedTypes.includes('module');
  const hasEvent  = purchasedTypes.includes('event');
  const hasTypes  = purchasedTypes.length > 0;

  // Build ordered action list: put what was actually purchased first. Landing is a
  // different origin than the dashboard, so every action is a full navigation there —
  // never an internal next/link route.
  type Action = { href: string; label: string; icon: React.ReactNode; primary: boolean };
  const actions: Action[] = [];

  if (!hasTypes || hasModule) {
    actions.push({ href: `${DASHBOARD_URL}/tests`,   label: t('viewTests'),   icon: <ClipboardCheck className="w-4 h-4" />,  primary: !hasTypes || (hasModule && !hasCourse && !hasEvent) });
  }
  if (!hasTypes || hasCourse) {
    actions.push({ href: `${DASHBOARD_URL}/courses`, label: t('viewCourses'), icon: <BookOpen className="w-4 h-4" />,        primary: hasCourse && !hasModule && !hasEvent });
  }
  if (hasEvent) {
    actions.push({ href: `${DASHBOARD_URL}/events`,  label: t('viewEvents'),  icon: <ArrowRight className="w-4 h-4" />,     primary: hasEvent && !hasModule && !hasCourse });
  }
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;

    const colors = ['#10b981', '#34d399', '#6ee7b7', '#0d9488', '#f59e0b', '#fbbf24'];
    // canvas-confetti defaults its canvas to z-index 100, which sits behind
    // this modal's z-[200] overlay — pin it above so the burst is visible.
    const zIndex = 300;

    // Two-sided streams for ~2.5s
    const end = Date.now() + 2500;
    const frame = () => {
      confetti({ particleCount: 3, angle: 60, spread: 70, origin: { x: 0, y: 0.6 }, colors, zIndex });
      confetti({ particleCount: 3, angle: 120, spread: 70, origin: { x: 1, y: 0.6 }, colors, zIndex });
      if (Date.now() < end) requestAnimationFrame(frame);
    };
    frame();

    // Center burst after a short delay
    setTimeout(() => {
      confetti({ particleCount: 80, spread: 100, origin: { y: 0.5 }, colors, scalar: 0.9, zIndex });
    }, 300);
  }, []);

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in slide-in-from-bottom-4 duration-300">
        {/* Top accent */}
        <div className="h-1.5 bg-gradient-to-r from-teal-400 via-emerald-400 to-teal-500" />

        <div className="p-8 text-center">
          {/* Icon */}
          <div className="relative w-20 h-20 mx-auto mb-5">
            <div className="absolute inset-0 bg-emerald-100 rounded-full animate-ping opacity-30" />
            <div className="relative w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center border-2 border-emerald-200">
              <CheckCircle className="w-10 h-10 text-emerald-500" strokeWidth={1.5} />
            </div>
          </div>

          <h2 className="text-2xl font-bold text-slate-900 mb-1">{t('title')}</h2>
          <p className="text-slate-500 text-sm mb-1">{t('subtitle')}</p>

          {/* Items */}
          {items && items.length > 0 && (
            <div className="mt-4 mb-6 text-left bg-slate-50 rounded-xl p-3 space-y-2 max-h-32 overflow-y-auto">
              {items.map((title, i) => (
                <div key={i} className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-1.5 shrink-0" />
                  <p className="text-xs text-slate-700 leading-snug">{title}</p>
                </div>
              ))}
            </div>
          )}
          {!items?.length && (
            <p className="text-xs text-slate-400 mb-6">{t('desc')}</p>
          )}

          {/* Actions — plain anchors, not next/link: these cross over to the dashboard origin */}
          <div className="flex flex-col gap-2 mt-2">
            {actions.map((action) => (
              <a
                key={action.href}
                href={action.href}
                onClick={onClose}
                className={`w-full py-3 rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${
                  action.primary
                    ? 'bg-teal-600 text-white hover:bg-teal-700 active:bg-teal-800'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {action.icon}
                {action.label}
              </a>
            ))}
            <button
              onClick={onClose}
              className="w-full py-2.5 text-xs text-slate-400 hover:text-slate-600 transition-colors"
            >
              {t('close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
